import React, { useRef, useState, useEffect, useCallback } from 'react'
import { useForm } from 'components/molecules/Form';
import Form from 'components/molecules/Form/Form';
import Field from 'components/molecules/Field';
import Button from 'components/molecules/Button';
import { EditProfileWrap } from 'components/atoms/Setting/Setting.styles';
import user_profile from 'assets/user-profile.png';
import edit_icon from 'assets/pencil-icon.svg';
import profileService from 'services/profileService';
import Toast from 'components/molecules/Toast';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setUser } from 'store/reducers/businessSlice';
import boy from "assets/Header/boy.svg";

const EditProfile = ({ onProfileUpdate }) => {
  const [form] = useForm();
  const [userProfile, setUserProfile] = useState(user_profile);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasFetchedRef = useRef(false);
  const onProfileUpdateRef = useRef(onProfileUpdate);

  useEffect(() => {
    onProfileUpdateRef.current = onProfileUpdate;
  }, [onProfileUpdate]);

  const fetchProfile = useCallback(async () => {
    // Prevent multiple simultaneous fetches
    if (hasFetchedRef.current) {
      console.log('Profile already fetched, skipping...');
      return;
    }
    try {
      setFetchingProfile(true);
      const response = await profileService.getProfile();
      
      if (response.success && response.data) {
        const userData = response.data;
        setProfileData(userData);
        hasFetchedRef.current = true;
        
        // Set profile picture
        if (userData.profilePicture) {
          setUserProfile(userData.profilePicture);
        }
        
        // Populate form fields
        form.setFieldsValue({
          'phone': userData.profile?.phone || '',
          'address': userData.profile?.address || '',
        });
        
        // Notify parent component (only once)
        if (onProfileUpdateRef.current && typeof onProfileUpdateRef.current === 'function') {
          onProfileUpdateRef.current(userData);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      
      if (error.message === 'UNAUTHORIZED') {
        Toast({
          type: 'error',
          message: 'Session expired. Please login again.',
        });
        navigate('/login');
      } else {
        Toast({
          type: 'error',
          message: error.message || 'Failed to load profile',
        });
      }
    } finally {
      setFetchingProfile(false);
    }
  }, [dispatch, form, navigate]);

  // Fetch user profile on component mount ONLY
  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchProfile();
    }
  }, [fetchProfile]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      Toast({
        type: 'error',
        message: 'Please upload a JPG or PNG image',
      });
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      Toast({
        type: 'error',
        message: 'Image size must be less than 5MB',
      });
      return;
    }

    try {
      setUploadingImage(true);
      
      // Show preview immediately
      const reader = new FileReader();
      reader.onload = () => {
        setUserProfile(reader.result);
      };
      reader.readAsDataURL(file);

      // Upload to server
      const response = await profileService.uploadProfilePicture(file);
      
      if (response.success && response.data) {
        Toast({
          type: 'success',
          message: 'Profile picture updated successfully!',
        });
        
        // Update profile picture URL from server
        if (response.data.profilePicture) {
          setUserProfile(response.data.profilePicture);
        }
        
        // Update Redux store
        dispatch(setUser(response.data));
        
        // Notify parent component
        if (onProfileUpdate) {
          onProfileUpdate(response.data);
        }
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      
      if (error.message === 'UNAUTHORIZED') {
        Toast({
          type: 'error',
          message: 'Session expired. Please login again.',
        });
        navigate('/login');
      } else {
        Toast({
          type: 'error',
          message: error.message || 'Failed to upload profile picture',
        });
      }
      
      // Revert to previous image on error
      if (profileData?.profilePicture) {
        setUserProfile(profileData.profilePicture);
      } else {
        setUserProfile(user_profile);
      }
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleIconClick = () => {
    if (!uploadingImage) {
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Prepare payload with only phone and address
      const payload = {};
      
      // Add optional fields only if they have values
      if (data['phone']) {
        payload.phone = data['phone'];
      }
      if (data['address']) {
        payload.address = data['address'];
      }
      
      const response = await profileService.updateProfile(payload);
      
      if (response.success && response.data) {
        Toast({
          type: 'success',
          message: 'Profile updated successfully!',
        });
        
        // Update local state
        setProfileData(response.data);
        
        // Update Redux store
        dispatch(setUser(response.data));
        
        // Notify parent component
        if (onProfileUpdate) {
          onProfileUpdate(response.data);
        }
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      
      if (error.message === 'UNAUTHORIZED') {
        Toast({
          type: 'error',
          message: 'Session expired. Please login again.',
        });
        navigate('/login');
      } else if (error.message === 'Email already exists') {
        Toast({
          type: 'error',
          message: 'This email is already in use by another account',
        });
      } else {
        Toast({
          type: 'error',
          message: error.message || 'Failed to update profile',
        });
      }
    } finally {
      setLoading(false);
    }
  };
  if (fetchingProfile) {
    return (
      <EditProfileWrap>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading profile...</p>
        </div>
      </EditProfileWrap>
    );
  }

  return (
    <EditProfileWrap>
      <div className="image-wrap">
        <img src={boy || userProfile} alt="user_profile" />
        {/* <img 
          src={edit_icon} 
          alt="edit_icon" 
          onClick={handleIconClick} 
          className='edit-icon'
          style={{ opacity: uploadingImage ? 0.5 : 1, cursor: uploadingImage ? 'not-allowed' : 'pointer' }}
        /> */}
        {uploadingImage && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '12px',
            color: '#666'
          }}>
            Uploading...
          </div>
        )}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept="image/jpeg, image/jpg, image/png"
          onChange={handleFileChange}
          disabled={uploadingImage}
        />
      </div>
      <Form form={form} onSubmit={handleSubmit}>
        <Form.Item
          label="Phone"
          type="tel"
          rounded
          name="phone"
          lg
          placeholder="+1234567890"
          $bgWhite
        >
          <Field disabled={loading} />
        </Form.Item>
        <Form.Item
          label="Address"
          type="text"
          rounded
          name="address"
          lg
          placeholder="123 Main St, City"
          $bgWhite
        >
          <Field disabled={loading} />
        </Form.Item>
        <div className="btn-holder">
          <Button lg disabled={loading || uploadingImage}>
            {loading ? 'Saving...' : 'Save Changes!'}
          </Button>
        </div>
      </Form>
    </EditProfileWrap>
  )
}

export default EditProfile
